import { useAuthStore } from '@/store/auth-store';
import { sessionRevision, sessionScope, sessionTenantId, requireCurrentSession } from '@/features/auth/session-scope';
import { getLocaleSnapshot } from '@/lib/i18n/runtime';
import { CallAPIError, type CallOwner } from './call-contract';

export interface CallSession extends CallOwner { scope: string; revision: number; language: string }
export const callUUID = (value: unknown): value is string => typeof value === 'string' && value !== '00000000-0000-0000-0000-000000000000' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(value);
export function captureCallSession(): CallSession {
  const state = useAuthStore.getState();
  return { user: state.user?.id ?? '', tenant: sessionTenantId(state), scope: sessionScope(state), revision: sessionRevision(), language: getLocaleSnapshot().locale };
}
export function currentCallSession(owner: CallSession, signal?: AbortSignal) {
  requireCurrentSession(owner.revision);
  signal?.throwIfAborted();
  if (sessionScope(useAuthStore.getState()) !== owner.scope) throw new DOMException('Aborted', 'AbortError');
}
export function isCurrentCallSession(owner: CallSession) {
  return owner.revision === sessionRevision() && owner.scope === sessionScope(useAuthStore.getState());
}
export function callAuthorization(owner: CallSession): string {
  currentCallSession(owner);
  const token = useAuthStore.getState().token;
  if (!token || !callUUID(owner.user) || !callUUID(owner.tenant)) throw new CallAPIError('unauthorized', false, 401);
  return token.startsWith('Bearer ') ? token : 'Bearer ' + token;
}
export function callKey(kind: string, owner = captureCallSession()) {
  return ['calls', kind, 'owner', owner.scope, owner.revision] as const;
}
export function ownsCallKey(key: readonly unknown[], owner: CallSession) {
  return key[0] === 'calls' && key[2] === 'owner' && key[3] === owner.scope && key[4] === owner.revision;
}
